import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { getCustomerStatusSummary } from 'services/customerStatusService';
import type { CustomerStatusBase } from 'services/customerStatusService';
import {
  getBasesTerritories,
  getFlagsFromTerritory,
} from 'services/baseTerritoryService';
import { getInstallBases } from 'services/basesServices';
import type { BaseFlags } from 'services/baseTerritoryService';
import type {
  BaseTerritoryData,
  InstallBaseData,
} from 'services/basesServices';
import {
  customerStatusFailure,
  customerStatusInvalidate,
  customerStatusRequest,
  customerStatusSuccess,
} from 'store/actions/customerStatus-actions';
import type { CustomerStatusState } from 'store/reducers/customerStatus-reducer';
import {
  BlockReason,
  UnverifiedBlock,
  resolveBlockDestination,
  resolveBlockReasons,
} from 'utils/blockReasons';
import type { BlockDestination } from 'utils/blockReasons';

export const CUSTOMER_STATUS_TTL_MS = 60_000;

const SITE_CODE_KEY = '@UltragazEmpresarial:customerSiteCode';

export const getSiteCode = (): string =>
  localStorage.getItem(SITE_CODE_KEY) ?? '';

type StoreShape = { customerStatusReducer: CustomerStatusState };

export type UseCustomerStatus = {
  status: CustomerStatusState['status'];
  reasons: BlockReason[];
  unverified: UnverifiedBlock[];
  summary: CustomerStatusState['summary'];
  territoryByBase: CustomerStatusState['territoryByBase'];
  flagsByBase: Record<string, BaseFlags>;
  isLoading: boolean;
  hasError: boolean;
  load: () => Promise<void>;
  refresh: () => Promise<BlockReason[]>;
  invalidate: () => void;
  destination: BlockDestination;
};

/** Evita duas cargas simultâneas quando home e pedido montam quase juntos. */
let inFlight: Promise<void> | null = null;

type TerritoriesResult = {
  territoryByBase: Record<string, BaseTerritoryData>;
  failed: string[];
};

/** Caminho novo: o território já vem dentro de cada base do resumo. */
const territoriesFromSummary = (
  bases: CustomerStatusBase[]
): TerritoriesResult => {
  const territoryByBase: Record<string, BaseTerritoryData> = {};
  const failed: string[] = [];

  bases.forEach((base) => {
    if (base.territory) {
      territoryByBase[base.name] = base.territory;
      return;
    }

    // Base sem território no resumo: não dá para verificar as flags dela.
    failed.push(base.name);
  });

  return { territoryByBase, failed };
};

/**
 * Caminho antigo, mantido como rede de segurança enquanto o contrato com
 * `bases` não está em todos os ambientes.
 *
 * @TODO STRY0050468: remover quando o `status-summary` com `bases` estiver em
 * produção — aí o N+1 de territory some de vez.
 */
const territoriesFromTerritoryApi = async (): Promise<TerritoriesResult> => {
  const bases = await getInstallBases({ includeInactive: true });
  const baseCodes = ((bases as InstallBaseData[]) ?? []).map(
    (base) => base.name
  );

  return getBasesTerritories(baseCodes);
};

export const useCustomerStatus = (): UseCustomerStatus => {
  const dispatch = useDispatch();
  const state = useSelector((store: StoreShape) => store.customerStatusReducer);

  const store = useStore<StoreShape>();
  const readState = useCallback(
    (): CustomerStatusState => store.getState().customerStatusReducer,
    [store]
  );

  const fetchAll = useCallback(
    async (siteCode: string): Promise<void> => {
      dispatch(customerStatusRequest(siteCode));

      const summaryResult = await getCustomerStatusSummary(siteCode);
      const summaryFailed = !summaryResult.ok;
      const summary = summaryResult.ok ? summaryResult.data : {};

      /*
       * O resumo passou a devolver as bases com o território embutido, numa
       * chamada só. Quando `bases` vem, não há mais N+1 de
       * `…/bases/{code}/territory`.
       */
      const { territoryByBase, failed } = summary.bases
        ? territoriesFromSummary(summary.bases)
        : // Gateway sem o campo `bases` (contrato anterior): volta ao
          // caminho base a base, para a tela não ficar sem as flags.
          await territoriesFromTerritoryApi();

      if (summaryFailed && Object.keys(territoryByBase).length === 0) {
        dispatch(customerStatusFailure(siteCode));
        return;
      }

      dispatch(
        customerStatusSuccess({
          siteCode,
          summary,
          summaryFailed,
          territoryByBase,
          failedBases: failed,
          fetchedAt: Date.now(),
        })
      );
    },
    [dispatch]
  );

  const run = useCallback(
    async (force: boolean): Promise<void> => {
      const siteCode = getSiteCode();
      if (!siteCode) return;

      const current = readState();
      const isFresh =
        !force &&
        current.status === 'ready' &&
        current.siteCode === siteCode &&
        current.fetchedAt !== null &&
        Date.now() - current.fetchedAt < CUSTOMER_STATUS_TTL_MS;

      if (isFresh) return;

      if (inFlight && !force) {
        await inFlight;
        return;
      }

      inFlight = fetchAll(siteCode).finally(() => {
        inFlight = null;
      });

      await inFlight;
    },
    [fetchAll, readState]
  );

  const load = useCallback((): Promise<void> => run(false), [run]);

  const refresh = useCallback(async (): Promise<BlockReason[]> => {
    await run(true);

    const current = readState();
    const currentFlags = Object.keys(current.territoryByBase).reduce<
      Record<string, BaseFlags>
    >(
      (acc, baseCode) => ({
        ...acc,
        [baseCode]: getFlagsFromTerritory(current.territoryByBase[baseCode]),
      }),
      {}
    );

    return resolveBlockReasons({
      summary: current.summary ?? undefined,
      flagsByBase: currentFlags,
      failedBases: current.failedBases,
      summaryFailed: current.summaryFailed || current.status === 'error',
    }).reasons;
  }, [run, readState]);

  const invalidate = useCallback((): void => {
    dispatch(customerStatusInvalidate());
  }, [dispatch]);

  const flagsByBase = useMemo(
    () =>
      Object.keys(state.territoryByBase).reduce<Record<string, BaseFlags>>(
        (acc, baseCode) => ({
          ...acc,
          [baseCode]: getFlagsFromTerritory(state.territoryByBase[baseCode]),
        }),
        {}
      ),
    [state.territoryByBase]
  );

  const { reasons, unverified } = useMemo(
    () =>
      resolveBlockReasons({
        summary: state.summary ?? undefined,
        flagsByBase,
        failedBases: state.failedBases,
        summaryFailed: state.summaryFailed || state.status === 'error',
      }),
    [
      state.summary,
      flagsByBase,
      state.failedBases,
      state.summaryFailed,
      state.status,
    ]
  );

  return {
    status: state.status,
    reasons,
    unverified,
    summary: state.summary,
    territoryByBase: state.territoryByBase,
    flagsByBase,
    isLoading: state.status === 'loading' || state.status === 'idle',
    hasError: state.status === 'error',
    load,
    refresh,
    invalidate,
    destination: resolveBlockDestination(reasons),
  };
};

export const useCustomerStatusOnEnter = (): UseCustomerStatus => {
  const customerStatus = useCustomerStatus();
  const { load } = customerStatus;

  useEffect(() => {
    load();
  }, [load]);

  return customerStatus;
};

export default useCustomerStatus;
